/**
 * Helpers for instanced 2D quad layers.
 *
 * Every visual layer in the game (terrain, units, turrets, health bars,
 * selection rings, projectiles, particles) is the same thing: a lot of screen
 * aligned quads that differ only in per-instance data. Building them all on one
 * helper keeps each layer to its shader and its buffer updates.
 */

import * as THREE from 'three/webgpu';
import { Fn, attribute, float, uv, vec2, vec4 } from 'three/tsl';

/** Node types an instanced attribute can carry. */
export type AttrType = 'float' | 'vec2' | 'vec3' | 'vec4';

/**
 * Typed wrapper around TSL's `attribute`.
 *
 * The upstream signature infers its type parameter from the argument, which
 * widens a string literal to `string` and loses every swizzle and operator on
 * the returned node. Pinning the parameter explicitly restores them.
 */
export function attr<T extends AttrType>(name: string, type: T) {
  return attribute<T>(name, type);
}

export interface QuadLayerOptions {
  capacity: number;
  /** Per-instance attribute names mapped to their component counts. */
  attributes: Record<string, number>;
  material: THREE.NodeMaterial;
  renderOrder?: number;
}

/**
 * A pool of instanced quads with CPU-side typed arrays the caller writes into
 * each frame. Capacity grows by reallocation, which is rare after warm-up.
 */
export class QuadLayer {
  readonly mesh: THREE.Mesh;
  readonly geometry: THREE.InstancedBufferGeometry;
  private arrays = new Map<string, Float32Array>();
  private attrs = new Map<string, THREE.InstancedBufferAttribute>();
  private sizes: Record<string, number>;
  private capacity: number;
  private count = 0;

  constructor(opts: QuadLayerOptions) {
    this.capacity = opts.capacity;
    this.sizes = opts.attributes;

    const quad = new THREE.PlaneGeometry(1, 1);
    this.geometry = new THREE.InstancedBufferGeometry();
    this.geometry.index = quad.index;
    this.geometry.setAttribute('position', quad.attributes.position);
    this.geometry.setAttribute('uv', quad.attributes.uv);
    quad.dispose();

    for (const [name, size] of Object.entries(opts.attributes)) {
      this.allocate(name, size, this.capacity);
    }
    this.geometry.instanceCount = 0;

    this.mesh = new THREE.Mesh(this.geometry, opts.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = opts.renderOrder ?? 0;
  }

  private allocate(name: string, size: number, capacity: number): void {
    const arr = new Float32Array(capacity * size);
    const attr = new THREE.InstancedBufferAttribute(arr, size);
    attr.setUsage(THREE.DynamicDrawUsage);
    this.arrays.set(name, arr);
    this.attrs.set(name, attr);
    this.geometry.setAttribute(name, attr);
  }

  /** Ensures room for `n` instances, reallocating buffers if needed. */
  reserve(n: number): void {
    if (n <= this.capacity) return;
    let cap = this.capacity;
    while (cap < n) cap *= 2;
    this.capacity = cap;
    for (const [name, size] of Object.entries(this.sizes)) {
      this.allocate(name, size, cap);
    }
  }

  array(name: string): Float32Array {
    return this.arrays.get(name)!;
  }

  /** Publishes `n` instances written into the arrays this frame. */
  commit(n: number): void {
    this.count = n;
    this.geometry.instanceCount = n;
    for (const attr of this.attrs.values()) {
      attr.needsUpdate = true;
      // Uploading only the used range keeps the transfer proportional to the
      // number of live instances rather than the capacity high-water mark.
      attr.updateRanges = [{ start: 0, count: n * attr.itemSize }];
    }
  }

  get length(): number {
    return this.count;
  }

  dispose(): void {
    this.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}

/**
 * Maps a quad's uv into one cell of a sprite atlas laid out as `cols` columns
 * by `rows` rows.
 *
 * The vertical term accounts for three.js uploading canvas textures flipped:
 * uv.y = 0 is the bottom of the quad and also the bottom of the atlas, so a
 * cell counted from the top of the canvas has to be mirrored back.
 */
export const atlasUV = /*#__PURE__*/ Fn(([col, row, cols, rows]: any[]) => {
  const stepX = float(1).div(cols);
  const stepY = float(1).div(rows);
  const u = col.add(uv().x).mul(stepX);
  const v = float(1).sub(row.add(float(1)).sub(uv().y).mul(stepY));
  return vec2(u, v);
});

/**
 * Replaces the magenta key colour in a sprite with a team colour, preserving
 * the shading that was painted on top of it.
 *
 * Detecting "how magenta is this pixel" rather than testing for an exact value
 * means the sprite art can be drawn with shadows and highlights over the key
 * colour and still tint correctly.
 */
export const applyTeamTint = /*#__PURE__*/ Fn(([texel, team]: any[]) => {
  const rgb = texel.rgb;
  const shade = rgb.r.min(rgb.b);
  const keyness = shade.sub(rgb.g).mul(2).clamp(0, 1);
  return vec4(rgb.mix(team.mul(shade), keyness), texel.a);
});
